import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ElectoralDistrict, Island, Page, PollingStation, PollingStationStaff, StaffRole } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, Tabs } from '../../ui/components';

const COMMISSIONER_ROLES = ['ELECTORAL_COMMISSIONER'];
const STATION_MANAGER_ROLES = ['ELECTORAL_COMMISSIONER', 'RETURNING_OFFICER'];
const ISLANDS: Island[] = ['MAHE', 'PRASLIN', 'LA_DIGUE'];
const STAFF_ROLES: StaffRole[] = ['PRESIDING_OFFICER', 'POLL_CLERK', 'COUNTING_OFFICER', 'SECURITY_OFFICER'];

/**
 * Electoral geography — the 26 standing National Assembly constituencies
 * (ElectoralDistrict) and their polling stations. Two tabs, one page:
 * districts rarely change; stations are the day-to-day management surface
 * (activate/close per election cycle, polling-day staff assignment).
 */
export function Geography() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isCommissioner = roles.some((r) => COMMISSIONER_ROLES.includes(r));
  const canManageStations = roles.some((r) => STATION_MANAGER_ROLES.includes(r));
  const [tab, setTab] = useState<'districts' | 'stations'>('districts');

  return (
    <div>
      <PageHeader title="Electoral Geography" subtitle="Electoral districts (constituencies) and their polling stations." />
      {!isCommissioner && !canManageStations ? <RoleNotice roles={[...COMMISSIONER_ROLES, ...STATION_MANAGER_ROLES]} /> : null}
      <Tabs
        tabs={[
          { key: 'districts', label: 'Electoral districts' },
          { key: 'stations', label: 'Polling stations' },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === 'districts' ? <DistrictsPanel isCommissioner={isCommissioner} /> : <StationsPanel isCommissioner={isCommissioner} canManageStations={canManageStations} />}
    </div>
  );
}

function DistrictsPanel({ isCommissioner }: { isCommissioner: boolean }) {
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const params = new URLSearchParams({ limit: '50' });
  if (q.trim()) params.set('q', q.trim());
  const listQ = useApiQuery<Page<ElectoralDistrict>>(`/api/v1/m39/districts?${params.toString()}`);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <Field label="Name or code contains" className="min-w-[220px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. beau-vallon" />
        </Field>
        {isCommissioner ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New district
          </button>
        ) : null}
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No electoral districts match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Name</th>
                <th className="table-head">Island</th>
                <th className="table-head">Seats</th>
                <th className="table-head">Active</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((d) => (
                <tr key={d.id}>
                  <td className="table-cell font-mono text-xs">{d.code}</td>
                  <td className="table-cell font-medium">{d.name}</td>
                  <td className="table-cell text-xs">{d.island.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs">{d.seatsAvailable}</td>
                  <td className="table-cell text-xs">{d.active ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <CreateDistrictModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateDistrictModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [island, setIsland] = useState<Island>('MAHE');
  const [seatsAvailable, setSeatsAvailable] = useState('1');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m39/districts', { code, name, island, seatsAvailable: Number.parseInt(seatsAvailable, 10) });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create district');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New electoral district" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Code (slug)">
          <input className="field-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. anse-royale" />
        </Field>
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Anse Royale" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Island">
            <select className="field-input" value={island} onChange={(e) => setIsland(e.target.value as Island)}>
              {ISLANDS.map((i) => (
                <option key={i} value={i}>
                  {i.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Seats available">
            <input type="number" className="field-input" value={seatsAvailable} onChange={(e) => setSeatsAvailable(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || code.trim().length < 2 || name.trim().length < 2}>
            {busy ? 'Creating…' : 'Create district'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function StationsPanel({ isCommissioner, canManageStations }: { isCommissioner: boolean; canManageStations: boolean }) {
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [staffFor, setStaffFor] = useState<PollingStation | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const params = new URLSearchParams({ limit: '50' });
  if (q.trim()) params.set('q', q.trim());
  const listQ = useApiQuery<Page<PollingStation>>(`/api/v1/m39/polling-stations?${params.toString()}`);
  const districtsQ = useApiQuery<Page<ElectoralDistrict>>('/api/v1/m39/districts?limit=50');
  const districtName = (id: string) => districtsQ.data?.items.find((d) => d.id === id)?.name ?? id;

  const doTransition = async (station: PollingStation, action: 'activate' | 'close') => {
    setBusy(station.id + action);
    setActionError(null);
    try {
      await api.post(`/api/v1/m39/polling-stations/${station.id}/${action}`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <Field label="Name or code contains" className="min-w-[220px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Victoria" />
        </Field>
        {isCommissioner ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New polling station
          </button>
        ) : null}
      </div>
      {actionError ? (
        <div className="mb-3">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No polling stations match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Name</th>
                <th className="table-head">District</th>
                <th className="table-head">Locality</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((s) => (
                <tr key={s.id}>
                  <td className="table-cell font-mono text-xs">{s.code}</td>
                  <td className="table-cell font-medium">{s.name}</td>
                  <td className="table-cell text-xs">{districtName(s.electoralDistrictId)}</td>
                  <td className="table-cell text-xs">{s.locality}</td>
                  <td className="table-cell">
                    <StatusBadge status={s.status} />
                  </td>
                  <td className="table-cell text-right">
                    {canManageStations ? (
                      <div className="flex justify-end gap-1.5">
                        {s.status === 'PLANNED' ? (
                          <button className="btn-success !px-2.5 !py-1 text-xs" disabled={busy !== null} onClick={() => doTransition(s, 'activate')}>
                            Activate
                          </button>
                        ) : null}
                        {s.status === 'ACTIVE' ? (
                          <button className="btn-danger !px-2.5 !py-1 text-xs" disabled={busy !== null} onClick={() => doTransition(s, 'close')}>
                            Close
                          </button>
                        ) : null}
                        <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setStaffFor(s)}>
                          Staff
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <CreateStationModal
          districts={districtsQ.data?.items ?? []}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {staffFor ? <StaffModal station={staffFor} onClose={() => setStaffFor(null)} /> : null}
    </div>
  );
}

function CreateStationModal({ districts, onClose, onDone }: { districts: ElectoralDistrict[]; onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [electoralDistrictId, setElectoralDistrictId] = useState(districts[0]?.id ?? '');
  const [addressLine, setAddressLine] = useState('');
  const [locality, setLocality] = useState('');
  const [capacity, setCapacity] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m39/polling-stations', {
        code,
        name,
        electoralDistrictId,
        addressLine,
        locality,
        capacity: capacity ? Number.parseInt(capacity, 10) : undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create polling station');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New polling station" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. VIC-01" />
          </Field>
          <Field label="Name">
            <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Victoria Primary School" />
          </Field>
        </div>
        <Field label="Electoral district">
          <select className="field-input" value={electoralDistrictId} onChange={(e) => setElectoralDistrictId(e.target.value)}>
            {districts.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Address">
          <input className="field-input" value={addressLine} onChange={(e) => setAddressLine(e.target.value)} placeholder="Street address" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Locality">
            <input className="field-input" value={locality} onChange={(e) => setLocality(e.target.value)} placeholder="e.g. Victoria" />
          </Field>
          <Field label="Capacity (optional)">
            <input type="number" className="field-input" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !code.trim() || !name.trim() || !electoralDistrictId || !addressLine.trim() || !locality.trim()}
          >
            {busy ? 'Creating…' : 'Create station'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function StaffModal({ station, onClose }: { station: PollingStation; onClose: () => void }) {
  const [electionId, setElectionId] = useState('');
  const [staffNin, setStaffNin] = useState('');
  const [staffName, setStaffName] = useState('');
  const [role, setRole] = useState<StaffRole>('PRESIDING_OFFICER');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const staffQ = useApiQuery<{ items: PollingStationStaff[] }>(
    electionId ? `/api/v1/m39/polling-stations/${station.id}/staff?electionId=${encodeURIComponent(electionId)}` : `/api/v1/m39/polling-stations/${station.id}/staff`,
  );

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m39/polling-stations/${station.id}/staff`, { electionId, staffNin, staffName, role });
      setStaffNin('');
      setStaffName('');
      staffQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to assign staff');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Polling-day staff — ${station.name}`} onClose={onClose} wide>
      <div className="space-y-4">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Election id (filters the list below and scopes new assignments)">
          <input className="field-input" value={electionId} onChange={(e) => setElectionId(e.target.value)} placeholder="paste the election's id from its detail page" />
        </Field>

        {staffQ.loading ? (
          <Spinner />
        ) : (staffQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No staff assigned yet" />
        ) : (
          <ul className="max-h-48 space-y-1.5 overflow-y-auto">
            {staffQ.data!.items.map((s) => (
              <li key={s.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
                <span>
                  {s.staffName} <span className="text-xs text-slate-400">({s.staffNin})</span>
                </span>
                <StatusBadge status={s.role} />
              </li>
            ))}
          </ul>
        )}

        <div className="rounded-lg border border-slate-200 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Assign staff</div>
          <div className="grid grid-cols-2 gap-2">
            <input className="field-input" placeholder="Staff NIN" value={staffNin} onChange={(e) => setStaffNin(e.target.value)} />
            <input className="field-input" placeholder="Staff name" value={staffName} onChange={(e) => setStaffName(e.target.value)} />
          </div>
          <div className="mt-2 flex items-center gap-2">
            <select className="field-input !w-auto" value={role} onChange={(e) => setRole(e.target.value as StaffRole)}>
              {STAFF_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
            <button className="btn-primary" onClick={submit} disabled={busy || !electionId.trim() || !staffNin.trim() || !staffName.trim()}>
              {busy ? 'Assigning…' : 'Assign'}
            </button>
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
